import * as React from 'react'

import {StyleValues} from './styles/types'
import {useInputFocus} from './hooks/useInputFocus'
import {useMouseover} from './hooks/useMouseover'

interface StyleProps {
  /** Style to apply to the component when the associated state is active (e.g. mouse hover, input focus). */
  conditionalStyle: StyleValues | undefined
}

/**
 * A div that applies additional styles when the mouse hovers over it.
 */
export function HoverDiv(rawProps: React.ComponentPropsWithoutRef<'div'> & StyleProps) {
  const {conditionalStyle, style, ...props} = rawProps

  const [isMouseOver, mouseListeners] = useMouseover()

  const hoverDivStyles = React.useMemo(
    () => ({...style, ...(isMouseOver && conditionalStyle)}),
    [style, conditionalStyle, isMouseOver]
  )

  return <div {...props} {...mouseListeners} style={hoverDivStyles} />
}

/**
 * An input box that applies additional styles when focus is placed on it.
 */
export const FocusInput = React.forwardRef(
  (props: React.ComponentPropsWithoutRef<'input'> & StyleProps, ref: React.Ref<HTMLInputElement>) => {
    const [isInputFocus, focusListeners] = useInputFocus()
    const {conditionalStyle, ...baseProps} = props
    return (
      <input
        ref={ref}
        {...baseProps}
        {...focusListeners}
        style={{...props.style, ...(isInputFocus && props.conditionalStyle)}}
      />
    )
  }
)

FocusInput.displayName = 'FocusInput'
